import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';

interface RaisedHand {
  userId: string;
  userName: string;
  raisedAt: string;
}

interface RaiseHandControlsProps {
  meetingCode: string;
  userId: string;
  userName: string;
  isHost: boolean;
}

export default function RaiseHandControls({
  meetingCode,
  userId,
  userName,
  isHost,
}: RaiseHandControlsProps) {
  const [raisedHands, setRaisedHands] = useState<RaisedHand[]>([]);
  const [myHandRaised, setMyHandRaised] = useState(false);
  const [showPanel, setShowPanel] = useState(false);
  const [loweringId, setLoweringId] = useState<string | null>(null);

  // Channel Realtime Supabase pour les mains levées
  useEffect(() => {
    const channel = supabase.channel(`raise-hand-${meetingCode}`, {
      config: { broadcast: { self: true } },
    });

    channel
      .on('broadcast', { event: 'hand-raised' }, (payload) => {
        const { userId, userName, raisedAt } = payload.payload as RaisedHand;
        setRaisedHands((prev) => {
          if (prev.find((h) => h.userId === userId)) return prev;
          return [...prev, { userId, userName, raisedAt }];
        });
      })
      .on('broadcast', { event: 'hand-lowered' }, (payload) => {
        const { userId } = payload.payload as { userId: string };
        setRaisedHands((prev) => prev.filter((h) => h.userId !== userId));
      })
      .on('broadcast', { event: 'all-hands-lowered' }, () => {
        setRaisedHands([]);
        setMyHandRaised(false);
      })
      .on('broadcast', { event: 'hand-lowered-by-host' }, (payload) => {
        const { userId } = payload.payload as { userId: string };
        setRaisedHands((prev) => prev.filter((h) => h.userId !== userId));
        if (userId === userId) setMyHandRaised(false);
      })
      .subscribe();

    return () => {
      channel.unsubscribe();
    };
  }, [meetingCode]);

  const raiseHand = useCallback(async () => {
    if (myHandRaised) return;

    setMyHandRaised(true);
    const channel = supabase.channel(`raise-hand-${meetingCode}`);
    await channel.subscribe();
    await channel.send({
      type: 'broadcast',
      event: 'hand-raised',
      payload: { userId, userName, raisedAt: new Date().toISOString() },
    });
    channel.unsubscribe();
  }, [meetingCode, userId, userName, myHandRaised]);

  const lowerHand = useCallback(async (targetUserId?: string) => {
    const targetId = targetUserId || userId;

    if (targetId === userId) setMyHandRaised(false);

    if (loweringId) return;
    setLoweringId(targetId);

    const channel = supabase.channel(`raise-hand-${meetingCode}`);
    await channel.subscribe();

    if (targetUserId) {
      // L'hôte baisse la main d'un participant
      await channel.send({
        type: 'broadcast',
        event: 'hand-lowered-by-host',
        payload: { userId: targetId },
      });
    } else {
      await channel.send({
        type: 'broadcast',
        event: 'hand-lowered',
        payload: { userId },
      });
    }
    channel.unsubscribe();
    setLoweringId(null);
  }, [meetingCode, userId, loweringId]);

  const lowerAllHands = useCallback(async () => {
    const channel = supabase.channel(`raise-hand-${meetingCode}`);
    await channel.subscribe();
    await channel.send({
      type: 'broadcast',
      event: 'all-hands-lowered',
      payload: {},
    });
    channel.unsubscribe();
    setMyHandRaised(false);
  }, [meetingCode]);

  const myHandIndex = raisedHands.findIndex((h) => h.userId === userId);

  return (
    <>
      {/* Bouton lever/baisser la main */}
      <div className="relative">
        {!myHandRaised ? (
          <button
            onClick={raiseHand}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-white/10 hover:bg-amber-500/30 rounded-lg text-xs font-medium text-white/80 hover:text-amber-300 transition-all cursor-pointer whitespace-nowrap"
            title="Lever la main"
          >
            <i className="ri-hand text-base"></i>
            <span className="hidden sm:inline">Lever la main</span>
          </button>
        ) : (
          <button
            onClick={() => lowerHand()}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-500/30 hover:bg-amber-500/40 rounded-lg text-xs font-medium text-amber-300 transition-all cursor-pointer whitespace-nowrap"
            title="Baisser la main"
          >
            <i className="ri-hand text-base animate-bounce"></i>
            <span className="hidden sm:inline">Main levée</span>
          </button>
        )}

        {/* Badge compteur de mains (visible par l'hôte) */}
        {isHost && raisedHands.length > 0 && (
          <span className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-amber-500 text-white rounded-full flex items-center justify-center text-xs font-bold shadow-lg">
            {raisedHands.length}
          </span>
        )}
      </div>

      {/* Panneau des mains levées (hôte uniquement) */}
      {isHost && raisedHands.length > 0 && (
        <>
          {/* Bouton pour ouvrir le panneau */}
          <button
            onClick={() => setShowPanel(!showPanel)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
              showPanel
                ? 'bg-amber-500/40 text-amber-300'
                : 'bg-white/10 hover:bg-amber-500/20 text-white/80 hover:text-amber-300'
            }`}
            title={`${raisedHands.length} main(s) levée(s)`}
          >
            <i className="ri-user-voice-line text-base"></i>
            <span className="hidden sm:inline">{raisedHands.length} main{raisedHands.length > 1 ? 's' : ''}</span>
          </button>

          {/* Overlay + panneau */}
          {showPanel && (
            <>
              <div
                className="fixed inset-0 z-40"
                onClick={() => setShowPanel(false)}
              ></div>
              <div className="absolute top-full right-0 mt-2 w-72 bg-gray-800 border border-white/10 rounded-xl shadow-2xl z-50 overflow-hidden animate-fadeIn">
                <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
                  <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                    <i className="ri-hand text-amber-400"></i>
                    Mains levées ({raisedHands.length})
                  </h3>
                  <button
                    onClick={lowerAllHands}
                    className="text-xs text-gray-400 hover:text-white transition-colors cursor-pointer"
                  >
                    Tout baisser
                  </button>
                </div>
                <div className="max-h-64 overflow-y-auto">
                  {raisedHands.map((hand, index) => (
                    <div
                      key={hand.userId}
                      className="flex items-center justify-between px-4 py-3 hover:bg-white/5 transition-colors border-b border-white/5 last:border-b-0"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center text-xs font-bold text-amber-300 flex-shrink-0">
                          {index + 1}
                        </span>
                        <div className="min-w-0">
                          <p className="text-sm text-white truncate">{hand.userName}</p>
                          <p className="text-xs text-gray-500">
                            {new Date(hand.raisedAt).toLocaleTimeString('fr-FR', {
                              hour: '2-digit',
                              minute: '2-digit',
                              second: '2-digit',
                            })}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => lowerHand(hand.userId)}
                        disabled={loweringId === hand.userId}
                        className="flex-shrink-0 px-3 py-1.5 bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 rounded-lg text-xs font-medium transition-colors cursor-pointer disabled:opacity-50 whitespace-nowrap"
                      >
                        {loweringId === hand.userId ? (
                          <i className="ri-loader-4-line animate-spin"></i>
                        ) : (
                          'Baisser'
                        )}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </>
      )}

      {/* Indicateur "main levée" pour les non-hôtes quand d'autres ont la main levée */}
      {!isHost && raisedHands.length > 0 && !myHandRaised && (
        <span className="text-xs text-amber-400/60 hidden sm:inline">
          {raisedHands.length} main{raisedHands.length > 1 ? 's' : ''}
        </span>
      )}
    </>
  );
}